import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Share2, CheckCircle, ArrowRight, Users, TrendingUp, MessageCircle, BarChart2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const QUALIFIER_URL = 'https://mikelmedia-qualify.paperform.co';
const BOOKING_URL = 'https://bookme.name/professionalchameleon/consultation';

const SocialMediaPage = () => {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const color = '#F2785C';

  const includes_en = [
    'Social media strategy & monthly content calendar',
    'Custom content creation: graphics, captions, hashtags',
    'Daily/weekly posting across Instagram, Facebook & LinkedIn',
    'Community management: comment replies & DM handling',
    'Instagram & Facebook Ads management (budget extra)',
    'Monthly analytics report & growth strategy review',
  ];

  const includes_es = [
    'Estrategia de redes sociales y calendario de contenido mensual',
    'Creación de contenido personalizado: gráficos, captions, hashtags',
    'Publicaciones diarias/semanales en Instagram, Facebook y LinkedIn',
    'Gestión de comunidad: respuesta a comentarios y DMs',
    'Gestión de anuncios en Instagram y Facebook (presupuesto aparte)',
    'Reporte mensual de analíticas y revisión de estrategia de crecimiento',
  ];

  const for_en = [
    'Business owners too busy to post consistently',
    'Brands wanting a professional, cohesive social presence',
    'Companies launching into new markets or audiences',
    'Anyone tired of posting with no engagement or results',
  ];

  const for_es = [
    'Dueños de negocios demasiado ocupados para publicar de forma consistente',
    'Marcas que quieren una presencia social profesional y cohesiva',
    'Empresas lanzándose en nuevos mercados o audiencias',
    'Quienes están cansados de publicar sin engagement ni resultados',
  ];

  const platforms = [
    { name: 'Instagram' },
    { name: 'Facebook' },
    { name: 'LinkedIn' },
    { name: 'TikTok' },
    { name: 'YouTube' },
  ];

  const metrics = [
    {
      icon: TrendingUp,
      en_title: 'Consistent Growth',
      es_title: 'Crecimiento Consistente',
      en_desc: 'Average 20–40% follower growth in 90 days for active clients.',
      es_desc: 'Crecimiento promedio del 20–40% de seguidores en 90 días para clientes activos.',
    },
    {
      icon: MessageCircle,
      en_title: 'Real Engagement',
      es_title: 'Engagement Real',
      en_desc: 'We build communities that comment, share, and buy — not just scroll.',
      es_desc: 'Construimos comunidades que comentan, comparten y compran — no solo hacen scroll.',
    },
    {
      icon: BarChart2,
      en_title: 'Data-Driven',
      es_title: 'Basado en Datos',
      en_desc: 'Monthly reports with clear metrics: reach, engagement, clicks, leads.',
      es_desc: 'Reportes mensuales con métricas claras: alcance, engagement, clics, clientes potenciales.',
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0F1C2E] flex flex-col">
      <Navbar />

      <main className="flex-1">

        {/* HERO */}
        <section
          className="pt-24 pb-20 px-4"
          style={{ background: `linear-gradient(135deg, ${color}18 0%, ${color}05 100%)` }}
        >
          <div className="max-w-5xl mx-auto">
            <Link
              to="/#servicios"
              className="inline-flex items-center gap-1 text-sm font-semibold mb-8 transition-opacity hover:opacity-70 font-source-sans"
              style={{ color }}
            >
              {t('service_page.back')}
            </Link>

            <div className="flex flex-col md:flex-row md:items-start gap-8">
              <div
                className="flex-shrink-0 w-20 h-20 flex items-center justify-center"
                style={{ backgroundColor: color + '20' }}
              >
                <Share2 size={36} style={{ color }} />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2.5 h-2.5" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                  <span className="text-xs font-bold tracking-widest uppercase font-poppins" style={{ color }}>
                    {isES ? 'Redes Sociales' : 'Social Media'}
                  </span>
                </div>

                <h1 className="font-poppins font-bold text-3xl sm:text-4xl lg:text-5xl text-[#233559] dark:text-white leading-tight mb-5">
                  {isES
                    ? 'Tu Presencia en Redes, Gestionada para Crecer'
                    : 'Your Social Media, Managed to Grow'}
                </h1>

                <p className="text-lg text-[#233559]/65 dark:text-white/60 leading-relaxed max-w-2xl font-source-sans">
                  {isES
                    ? 'Nos encargamos de tus redes de principio a fin — estrategia, contenido, publicaciones y comunidad — para que tú te concentres en lo que haces mejor: dirigir tu negocio.'
                    : 'We handle your social media end-to-end — strategy, content, posting, and community — so you can focus on what you do best: running your business.'}
                </p>

                <p className="mt-4 text-sm font-semibold font-source-sans" style={{ color }}>
                  {isES ? 'Desde $500 / mes' : 'Starting at $500 / month'}
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-8">
                  <a
                    href={QUALIFIER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3.5 font-poppins font-semibold text-white text-sm tracking-wide transition-opacity hover:opacity-90"
                    style={{ backgroundColor: color }}
                  >
                    {isES ? 'Quiero hacer crecer mis redes' : 'Grow my social media'}
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3.5 font-poppins font-semibold text-sm tracking-wide border-2 transition-opacity hover:opacity-80"
                    style={{ color, borderColor: color, backgroundColor: 'transparent' }}
                  >
                    {isES ? 'Reservar consulta' : 'Book a consultation'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PLATFORM BAR */}
        <section className="py-8 px-4 bg-[#233559] dark:bg-[#1A2639]">
          <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-6">
            <span className="text-white/40 text-xs font-poppins uppercase tracking-wider mr-2">
              {isES ? 'Plataformas' : 'Platforms'}
            </span>
            {platforms.map((p, i) => (
              <span key={i} className="font-poppins font-bold text-white/70 text-sm">
                {p.name}
              </span>
            ))}
          </div>
        </section>

        {/* WHAT'S INCLUDED + WHO IT'S FOR */}
        <section className="py-20 px-4 bg-white dark:bg-[#0F1C2E]">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">

            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.includes_title')}
                </h2>
              </div>
              <ul className="space-y-4">
                {includes_en.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color }} />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">
                      {isES ? includes_es[idx] : item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.for_title')}
                </h2>
              </div>
              <ul className="space-y-4">
                {for_en.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Users size={20} className="flex-shrink-0 mt-0.5" style={{ color }} />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">
                      {isES ? for_es[idx] : item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* WHY IT WORKS */}
        <section className="py-20 px-4" style={{ backgroundColor: color + '08' }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <span className="text-xs font-bold tracking-widest uppercase font-poppins" style={{ color }}>
                  {isES ? 'Por qué funciona' : 'Why it works'}
                </span>
              </div>
              <h2 className="font-poppins font-bold text-3xl text-[#233559] dark:text-white">
                {isES ? 'Resultados Reales, No Solo Likes' : 'Real Results, Not Just Likes'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {metrics.map(({ icon: Icon, en_title, es_title, en_desc, es_desc }, i) => (
                <div key={i} className="bg-white dark:bg-[#1A2639] p-7 border border-[#233559]/08 dark:border-white/08">
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-5"
                    style={{ backgroundColor: color + '20' }}
                  >
                    <Icon size={22} style={{ color }} />
                  </div>
                  <h3 className="font-poppins font-bold text-lg text-[#233559] dark:text-white mb-2">
                    {isES ? es_title : en_title}
                  </h3>
                  <p className="text-[#233559]/65 dark:text-white/55 font-source-sans text-sm leading-relaxed">
                    {isES ? es_desc : en_desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="py-20 px-4" style={{ backgroundColor: color }}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-white mb-4">
              {isES
                ? '¿Listo para que tus redes por fin trabajen para ti?'
                : 'Ready for social media that finally works for you?'}
            </h2>
            <p className="text-white/80 text-lg mb-8 font-source-sans">
              {isES
                ? 'Reserva tu consulta directamente o aplica en menos de 2 minutos.'
                : 'Book your consultation directly or apply in less than 2 minutes.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white font-poppins font-bold text-sm tracking-wide transition-opacity hover:opacity-90"
                style={{ color }}
              >
                {isES ? 'Reservar consulta' : 'Book a consultation'}
                <ArrowRight size={16} />
              </a>
              <a
                href={QUALIFIER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 font-poppins font-semibold text-sm tracking-wide border-2 border-white/60 text-white transition-opacity hover:opacity-80"
              >
                {isES ? 'Aplicar primero' : 'Apply first'}
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default SocialMediaPage;
